import type { Project } from '../../types/project';
import type { JobCardRecord } from './jobCard.types';
import { asProjectMetadata } from './jobCardSurveyNotes';

export const NO_INSTALL_DATE_KEY = 'none';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2})?/;

export function parseStamp(value: string) {
  if (value && !value.match(/(Z|[+-][0-9]{2}:?[0-9]{2})$/)) {
    return new Date(`${value}Z`);
  }
  return new Date(value);
}

export function londonDayKey(value?: string | null): string {
  if (!value) return NO_INSTALL_DATE_KEY;
  const match = String(value).trim().match(DATE_ONLY);
  if (match && String(value).trim().length === 10) return `${match[1]}-${match[2]}-${match[3]}`;
  const date = parseStamp(value);
  if (Number.isNaN(date.getTime())) return NO_INSTALL_DATE_KEY;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (!year || !month || !day) return NO_INSTALL_DATE_KEY;
  return `${year}-${month}-${day}`;
}

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;
const UTC_MIDNIGHT = /T00:00:00(\.0+)?(Z|\+00:?00)$/i;

export function toInstallDateOnly(value?: string | null): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const match = raw.match(DATE_ONLY);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const local = HAS_ZONE.test(raw) ? null : raw.match(DATE_TIME_LOCAL);
  if (local) return local[1];
  if (UTC_MIDNIGHT.test(raw)) return raw.slice(0, 10);
  const key = londonDayKey(raw);
  return key === NO_INSTALL_DATE_KEY ? null : key;
}

export function toInstallTimeOnly(value?: string | null): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const local = HAS_ZONE.test(raw) ? null : raw.match(DATE_TIME_LOCAL);
  if (local) return local[2] === '00:00' ? null : local[2];
  if (DATE_ONLY.test(raw) || /T12:00:00Z$/.test(raw) || UTC_MIDNIGHT.test(raw)) return null;
  const date = parseStamp(raw);
  if (Number.isNaN(date.getTime())) return null;
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/London',
  });
  return time === '00:00' ? null : time;
}

export function toInstallDateTimeLocal(value?: string | null): string | null {
  const date = toInstallDateOnly(value);
  if (!date) return null;
  const time = toInstallTimeOnly(value);
  return time ? `${date}T${time}` : date;
}

export function withInstallTime(dateOnly: string | null, time?: string | null): string | null {
  const date = toInstallDateOnly(dateOnly);
  if (!date) return null;
  const match = String(time || '').trim().match(/^(\d{2}:\d{2})/);
  return match ? `${date}T${match[1]}` : date;
}

export function todayInstallDateOnly(now = new Date()): string {
  return londonDayKey(now.toISOString());
}

const START_MAP = 'job_card_install_overrides';
/** Last-day stamps with an end time; the date-only column cannot hold the time. */
const END_MAP = 'job_card_install_end_overrides';

function perCardInstallOverride(
  project: { metadata?: unknown },
  jobCardId: string,
  key: string = START_MAP,
): string | null | undefined {
  const map = (asProjectMetadata({ metadata: project.metadata as Project['metadata'] }) as Record<string, unknown>)[key];
  if (!map || typeof map !== 'object' || Array.isArray(map)) return undefined;
  const record = map as Record<string, unknown>;
  if (!(jobCardId in record)) return undefined;
  return toInstallDateTimeLocal(record[jobCardId] == null ? null : String(record[jobCardId]));
}

export function installDateOverrideFrom(
  jobCard: Pick<JobCardRecord, 'id' | 'install_date_override'>,
  project: { metadata?: unknown },
): string | null {
  const fromRow = toInstallDateOnly(jobCard.install_date_override);
  const mapped = perCardInstallOverride(project, jobCard.id);
  if (mapped && mapped.includes('T')) return mapped;
  if (fromRow && mapped && mapped.startsWith(fromRow)) return mapped;
  if (fromRow) return fromRow;
  return mapped === undefined ? null : mapped;
}

/** A card's last on-site day, with its end time when one was set. */
export function installEndOverrideFrom(
  jobCard: Pick<JobCardRecord, 'id' | 'install_end_date_override'>,
  project: { metadata?: unknown },
): string | null {
  const fromRow = toInstallDateOnly(jobCard.install_end_date_override);
  if (!fromRow) return null;
  const mapped = perCardInstallOverride(project, jobCard.id, END_MAP);
  // A time saved against an older end date no longer applies
  return mapped && mapped.startsWith(`${fromRow}T`) ? mapped : fromRow;
}

function withMapEntry(meta: Record<string, unknown>, key: string, jobCardId: string, value: string | null) {
  const current = meta[key];
  const map = current && typeof current === 'object' && !Array.isArray(current)
    ? { ...(current as Record<string, unknown>) }
    : {};
  if (value) map[jobCardId] = value;
  else delete map[jobCardId];
  return map;
}

/**
 * Project metadata with a card's start stamp (and, when `endStamp` is passed, its end stamp)
 * recorded. Clearing the start clears the end time too.
 */
export function withPerCardInstallOverride(
  project: { metadata?: unknown },
  jobCardId: string,
  stamp: string | null,
  endStamp?: string | null,
): Record<string, unknown> {
  const meta = asProjectMetadata({ metadata: project.metadata as Project['metadata'] }) as Record<string, unknown>;
  const next = toInstallDateTimeLocal(stamp);
  const end = next && endStamp !== undefined ? toInstallDateTimeLocal(endStamp) : null;
  const keepEnd = !!next && endStamp === undefined;
  return {
    ...meta,
    [START_MAP]: withMapEntry(meta, START_MAP, jobCardId, next),
    ...(keepEnd ? {} : { [END_MAP]: withMapEntry(meta, END_MAP, jobCardId, end && end.includes('T') ? end : null) }),
  };
}

export function effectiveInstallDate(
  scheduled?: string | null,
  overrideStamp?: string | null,
): string | null {
  const local = toInstallDateTimeLocal(overrideStamp);
  if (local) return local.includes('T') ? `${local}:00` : local;
  return scheduled || null;
}

export function formatOverrideDateLabel(dateOnly: string): string {
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return dateOnly;
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/London',
  });
}

export function formatInstallStamp(value?: string | null): string | null {
  const local = toInstallDateTimeLocal(value);
  if (!local) return null;
  const date = toInstallDateOnly(local);
  const time = toInstallTimeOnly(local);
  if (!date) return null;
  const dateLabel = formatOverrideDateLabel(date);
  return time ? `${dateLabel}, ${time}` : dateLabel;
}

export type InstallCalendarEvent = {
  event_date?: string | null;
  end_date?: string | null;
  metadata?: unknown;
};

export type InstallSpan = {
  /** Day 1 stamp (date or local date-time). */
  start: string | null;
  /** On-site day keys, ascending. Weekends inside the range are skipped. */
  days: string[];
  overridden: boolean;
  /** Finish time on the last day (HH:MM), when the override sets one. */
  endTime?: string | null;
};

const MAX_INSTALL_SPAN_DAYS = 60;

export function shiftDayKey(dayKey: string, offset: number): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + offset, 12)).toISOString().slice(0, 10);
}

function isWeekendDayKey(dayKey: string): boolean {
  const [year, month, day] = dayKey.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day, 12)).getUTCDay();
  return weekday === 0 || weekday === 6;
}

function workDaysBetween(startDay: string, endDay: string): string[] {
  if (endDay <= startDay) return [startDay];
  const days: string[] = [];
  let day = startDay;
  for (let count = 0; day <= endDay && count < MAX_INSTALL_SPAN_DAYS; count += 1) {
    if (day === startDay || day === endDay || !isWeekendDayKey(day)) days.push(day);
    day = shiftDayKey(day, 1);
  }
  return days;
}

function additionalWorkRanges(metadata: unknown): Array<[string, string]> {
  if (!metadata || typeof metadata !== 'object') return [];
  const ranges = (metadata as { additionalWorkRanges?: unknown }).additionalWorkRanges;
  if (!Array.isArray(ranges)) return [];
  return ranges.flatMap((range) => {
    const start = toInstallDateOnly((range as { startDate?: string | null })?.startDate);
    if (!start) return [];
    const end = toInstallDateOnly((range as { endDate?: string | null })?.endDate) || start;
    return [[start, end] as [string, string]];
  });
}

export function jobCardInstallSpan(input: {
  calendar?: InstallCalendarEvent | null;
  fallbackStart?: string | null;
  overrideStart?: string | null;
  overrideEnd?: string | null;
  /** False for cards after job card 1: they have only their own dates, never the booking's. */
  followsBooking?: boolean;
}): InstallSpan {
  if (input.followsBooking === false) {
    return jobCardInstallSpan({ overrideStart: input.overrideStart, overrideEnd: input.overrideEnd });
  }
  const overrideStartDay = toInstallDateOnly(input.overrideStart);
  const overrideEndDay = toInstallDateOnly(input.overrideEnd);
  const calendarStart = input.calendar?.event_date || input.fallbackStart || null;
  const start = effectiveInstallDate(calendarStart, input.overrideStart);
  const startDay = toInstallDateOnly(start);
  if (!startDay) return { start: null, days: [], overridden: false };
  const overridden = !!overrideStartDay || !!overrideEndDay;
  if (overridden) {
    const endDay = overrideEndDay
      || (overrideStartDay ? startDay : toInstallDateOnly(input.calendar?.end_date) || startDay);
    const endTime = toInstallTimeOnly(start) ? toInstallTimeOnly(input.overrideEnd) : null;
    return { start, days: workDaysBetween(startDay, endDay), overridden, endTime };
  }
  const endDay = toInstallDateOnly(input.calendar?.end_date) || startDay;
  const days = new Set(workDaysBetween(startDay, endDay));
  additionalWorkRanges(input.calendar?.metadata).forEach(([from, to]) => {
    workDaysBetween(from, to).forEach((day) => days.add(day));
  });
  return { start, days: Array.from(days).sort(), overridden };
}

/** Day a job should be listed under: today (or next on-site day) while in progress, else day 1. */
export function installDisplayDay(days: string[], todayKey: string): string {
  if (!days.length) return NO_INSTALL_DATE_KEY;
  return days.find((day) => day >= todayKey) || days[0];
}

export function formatShortDay(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London',
  });
}

/** Groups days into back-to-back runs. Weekend gaps do not break a run. */
export function dayRuns(days: string[]): Array<[string, string]> {
  const sorted = Array.from(new Set(days)).sort();
  const ranges: Array<[string, string]> = [];
  sorted.forEach((day) => {
    const current = ranges[ranges.length - 1];
    if (current) {
      let next = shiftDayKey(current[1], 1);
      while (next < day && isWeekendDayKey(next)) next = shiftDayKey(next, 1);
      if (next === day) {
        current[1] = day;
        return;
      }
    }
    ranges.push([day, day]);
  });
  return ranges;
}

/** "Mon 28 Sept – Tue 29 Sept, Fri 2 Oct". Weekend gaps do not break a range. */
export function formatDayRanges(days: string[]): string {
  return dayRuns(days)
    .map(([from, to]) => (from === to ? formatShortDay(from) : `${formatShortDay(from)} – ${formatShortDay(to)}`))
    .join(', ');
}

/** Lists the on-site days when they have a gap, so "until Fri" never hides a day off in between. */
export function installSpanNote(days: string[], dayKey?: string | null): string | null {
  if (days.length < 2) return null;
  const when = dayRuns(days).length > 1
    ? `on site ${formatDayRanges(days)}`
    : `until ${formatShortDay(days[days.length - 1])}`;
  const index = dayKey ? days.indexOf(dayKey) : -1;
  return index >= 0
    ? `Day ${index + 1} of ${days.length} · ${when}`
    : `${days.length} days · ${when}`;
}

/** Shown when a card covers only part of the calendar invite, e.g. after a crew swap. */
export function wholeJobNote(cardDays: string[], jobDays: string[]): string | null {
  if (jobDays.length < 2 || !cardDays.length) return null;
  const sameRange = cardDays[0] === jobDays[0] && cardDays[cardDays.length - 1] === jobDays[jobDays.length - 1];
  if (sameRange) return null;
  return `Whole job: ${formatShortDay(jobDays[0])} – ${formatShortDay(jobDays[jobDays.length - 1])}`;
}

export function formatInstallSpan(span: InstallSpan): string | null {
  const startLabel = formatInstallStamp(span.start);
  if (!startLabel) return null;
  if (span.days.length < 2) return span.endTime ? `${startLabel}–${span.endTime}` : startLabel;
  const last = formatOverrideDateLabel(span.days[span.days.length - 1]);
  const lastLabel = span.endTime ? `${last}, ${span.endTime}` : last;
  return `${startLabel} → ${lastLabel} (${span.days.length} days)`;
}
